import React, { useState, useMemo } from "react";
import { X, Info } from "lucide-react";
import {
  WILDCARD_IMG,
  WILDCARD_TR,
  BANNER_IMG_MAP,
  getRewardsBetween,
  getAllRewards,
  summarizeRewards,
} from "@/lib/collectionRewards";

// Ödül görsel yolları (public/rewards/*.png)
const IMG_GEM = "/rewards/gem.png";
const IMG_CHEST5 = "/rewards/chest5.png";
const IMG_EVO = "/rewards/evo-chest.png";
const IMG_TOWER = "/rewards/tower.png";
const IMG_BANNER_DEFAULT = "/rewards/banner-prince.png";

const RARITY_TXT = {
  common: "text-slate-200",
  rare: "text-orange-300",
  epic: "text-fuchsia-300",
  legendary: "text-rose-300",
  champion: "text-amber-300",
};
const RARITY_BORDER = {
  common: "border-slate-300/30",
  rare: "border-orange-400/40",
  epic: "border-fuchsia-400/40",
  legendary: "border-rose-400/40",
  champion: "border-amber-400/50",
};
const RARITY_BG = {
  common: "bg-slate-400/[0.06]",
  rare: "bg-orange-500/[0.06]",
  epic: "bg-fuchsia-500/[0.06]",
  legendary: "bg-rose-500/[0.06]",
  champion: "bg-amber-500/[0.06]",
};

const fmt = (n) => (n ?? 0).toLocaleString("tr-TR");

// Küçük ödül kartı (bir seviye = bir ödül)
function RewardCard({ r, compact = false }) {
  const size = compact ? "h-10 w-10" : "h-12 w-12";
  const box = compact ? "px-2 py-1.5" : "px-2.5 py-2";
  if (r.type === "joker") {
    return (
      <div className={`rounded-lg border ${RARITY_BORDER[r.rarity]} ${RARITY_BG[r.rarity]} ${box} flex items-center gap-2`}>
        <img src={WILDCARD_IMG[r.rarity]} alt={WILDCARD_TR[r.rarity]} className={`${size} object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] shrink-0`} />
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Sv {r.level}</div>
          <div className={`text-[11px] font-bold leading-tight truncate ${RARITY_TXT[r.rarity]}`}>
            {fmt(r.qty)}× {WILDCARD_TR[r.rarity].replace(" Joker", "")}
          </div>
        </div>
      </div>
    );
  }
  if (r.type === "gem") {
    return (
      <div className={`rounded-lg border border-emerald-400/30 bg-emerald-500/[0.06] ${box} flex items-center gap-2`}>
        <img src={IMG_GEM} alt="Taş" className={`${size} object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] shrink-0`} />
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Sv {r.level}</div>
          <div className="text-[11px] font-bold text-emerald-300 leading-tight">{fmt(r.qty)} Taş</div>
        </div>
      </div>
    );
  }
  if (r.type === "chest5") {
    return (
      <div className={`rounded-lg border border-amber-400/40 bg-amber-500/[0.06] ${box} flex items-center gap-2`}>
        <img src={IMG_CHEST5} alt="5 Yıldızlı Sandık" className={`${size} object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] shrink-0`} />
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Sv {r.level}</div>
          <div className="text-[11px] font-bold text-amber-200 leading-tight">5 Yıldızlı Sandık</div>
        </div>
      </div>
    );
  }
  if (r.type === "evoChest") {
    return (
      <div className={`rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/[0.06] ${box} flex items-center gap-2`}>
        <img src={IMG_EVO} alt="Evrim Sandığı" className={`${size} object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] shrink-0`} />
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Sv {r.level}</div>
          <div className="text-[11px] font-bold text-fuchsia-300 leading-tight">Evrim Sandığı</div>
        </div>
      </div>
    );
  }
  if (r.type === "banner") {
    const img = BANNER_IMG_MAP[r.label] || IMG_BANNER_DEFAULT;
    return (
      <div className={`rounded-lg border border-blue-400/30 bg-blue-500/[0.06] ${box} flex items-center gap-2`}>
        <img src={img} alt={r.label} className={`${size} object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] shrink-0`} />
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Sv {r.level}</div>
          <div className="text-[11px] font-bold text-blue-200 leading-tight truncate">{r.label}</div>
        </div>
      </div>
    );
  }
  // tower
  return (
    <div className={`rounded-lg border border-purple-400/30 bg-purple-500/[0.06] ${box} flex items-center gap-2`}>
      <img src={IMG_TOWER} alt={r.label} className={`${size} object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] shrink-0`} />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Sv {r.level}</div>
        <div className="text-[11px] font-bold text-purple-200 leading-tight truncate">{r.label}</div>
      </div>
    </div>
  );
}

// Özet çipleri (Toplam Taş, Sandık, Joker vs.)
function RewardsSummary({ list }) {
  const s = useMemo(() => summarizeRewards(list), [list]);
  const chips = [];
  if (s.gems > 0) chips.push({ key: "g", el: (
    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 border border-emerald-400/30 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">
      <img src={IMG_GEM} alt="" className="h-4 w-4 object-contain" /> {fmt(s.gems)} Taş
    </span>
  )});
  if (s.chest5 > 0) chips.push({ key: "c5", el: (
    <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 border border-amber-400/40 px-1.5 py-0.5 text-[10px] font-bold text-amber-200">
      <img src={IMG_CHEST5} alt="" className="h-4 w-4 object-contain" /> {s.chest5}× 5 Yıldızlı
    </span>
  )});
  if (s.evoChest > 0) chips.push({ key: "ec", el: (
    <span className="inline-flex items-center gap-1 rounded-md bg-fuchsia-500/15 border border-fuchsia-400/30 px-1.5 py-0.5 text-[10px] font-bold text-fuchsia-300">
      <img src={IMG_EVO} alt="" className="h-4 w-4 object-contain" /> {s.evoChest}× Evrim
    </span>
  )});
  ["common", "rare", "epic", "legendary", "champion"].forEach((r) => {
    const n = s.jokers[r] || 0;
    if (n <= 0) return;
    chips.push({ key: `j-${r}`, el: (
      <span className={`inline-flex items-center gap-1 rounded-md border ${RARITY_BORDER[r]} ${RARITY_BG[r]} px-1.5 py-0.5 text-[10px] font-bold ${RARITY_TXT[r]}`}>
        <img src={WILDCARD_IMG[r]} alt="" className="h-4 w-4 object-contain" />
        {fmt(n)}
      </span>
    )});
  });
  s.banners.forEach((b, i) => chips.push({ key: `b-${i}`, el: (
    <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/15 border border-blue-400/30 px-1.5 py-0.5 text-[10px] font-bold text-blue-200">
      <img src={BANNER_IMG_MAP[b.label] || IMG_BANNER_DEFAULT} alt="" className="h-4 w-4 object-contain" /> {b.label}
    </span>
  )}));
  s.towers.forEach((t, i) => chips.push({ key: `t-${i}`, el: (
    <span className="inline-flex items-center gap-1 rounded-md bg-purple-500/15 border border-purple-400/30 px-1.5 py-0.5 text-[10px] font-bold text-purple-200">
      <img src={IMG_TOWER} alt="" className="h-4 w-4 object-contain" /> {t.label}
    </span>
  )}));
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c) => <React.Fragment key={c.key}>{c.el}</React.Fragment>)}
    </div>
  );
}

/**
 * Koleksiyon Seviyesi Ödül Paneli
 * Ana panel: sadece TOPLAM özet çipleri, yatay kayan marquee.
 * Info butonu: her seviye ödülünü gösteren modal.
 */
export default function CollectionRewardsPanel({ current, target }) {
  const [showAll, setShowAll] = useState(false);
  const between = useMemo(() => getRewardsBetween(current, target), [current, target]);
  const all = useMemo(() => getAllRewards(), []);
  const betweenSummary = useMemo(() => summarizeRewards(between), [between]);

  // Marquee chip listesi (renderable JSX'ler)
  const marqueeChips = useMemo(() => {
    const arr = [];
    if (betweenSummary.gems > 0) arr.push(
      <span key="g" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-3 py-1.5 text-xs font-bold text-emerald-300 whitespace-nowrap">
        <img src={IMG_GEM} alt="" className="h-5 w-5 object-contain" /> {fmt(betweenSummary.gems)} Taş
      </span>
    );
    if (betweenSummary.chest5 > 0) arr.push(
      <span key="c5" className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-3 py-1.5 text-xs font-bold text-amber-200 whitespace-nowrap">
        <img src={IMG_CHEST5} alt="" className="h-5 w-5 object-contain" /> {betweenSummary.chest5}× 5 Yıldızlı Sandık
      </span>
    );
    if (betweenSummary.evoChest > 0) arr.push(
      <span key="ec" className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/40 px-3 py-1.5 text-xs font-bold text-fuchsia-300 whitespace-nowrap">
        <img src={IMG_EVO} alt="" className="h-5 w-5 object-contain" /> {betweenSummary.evoChest}× Evrim Sandığı
      </span>
    );
    ["common", "rare", "epic", "legendary", "champion"].forEach((r) => {
      const n = betweenSummary.jokers[r] || 0;
      if (n <= 0) return;
      arr.push(
        <span key={`j-${r}`} className={`inline-flex items-center gap-1.5 rounded-full border ${RARITY_BORDER[r]} ${RARITY_BG[r]} px-3 py-1.5 text-xs font-bold ${RARITY_TXT[r]} whitespace-nowrap`}>
          <img src={WILDCARD_IMG[r]} alt="" className="h-5 w-5 object-contain" />
          {fmt(n)}× {WILDCARD_TR[r].replace(" Joker", "")}
        </span>
      );
    });
    betweenSummary.banners.forEach((b, i) => arr.push(
      <span key={`b-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 border border-blue-400/40 px-3 py-1.5 text-xs font-bold text-blue-200 whitespace-nowrap">
        <img src={BANNER_IMG_MAP[b.label] || IMG_BANNER_DEFAULT} alt="" className="h-5 w-5 object-contain" /> {b.label}
      </span>
    ));
    betweenSummary.towers.forEach((t, i) => arr.push(
      <span key={`t-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-purple-500/15 border border-purple-400/40 px-3 py-1.5 text-xs font-bold text-purple-200 whitespace-nowrap">
        <img src={IMG_TOWER} alt="" className="h-5 w-5 object-contain" /> {t.label}
      </span>
    ));
    return arr;
  }, [betweenSummary]);

  // Marquee süresi: chip sayısına göre yavaş, sabit hız
  const marqueeDurationSec = Math.max(15, marqueeChips.length * 3);

  return (
    <>
      <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.04] px-3 py-2.5" data-testid="cl-rewards-panel">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-amber-300/90 font-bold shrink-0">
              Kazanılacak Ödüller
            </div>
            {target > current && (
              <span className="text-[9px] text-muted-foreground tabular-nums truncate">
                (Sv {current + 1} → {target})
              </span>
            )}
          </div>
          <button
            onClick={() => setShowAll(true)}
            data-testid="cl-rewards-info-btn"
            className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-2 py-1 text-[10px] font-bold text-primary shrink-0"
            title="Tüm ödülleri gör"
          >
            <Info className="h-3 w-3" /> Tümü
          </button>
        </div>

        {marqueeChips.length === 0 ? (
          <div className="text-[11px] text-muted-foreground py-1">
            Bu aralıkta ödül yok. (İlk ödül Sv 20&apos;de)
          </div>
        ) : (
          <div
            className="cl-marquee-wrap relative overflow-hidden group"
            data-testid="cl-rewards-marquee"
            style={{ "--marquee-duration": `${marqueeDurationSec}s` }}
          >
            {/* iki set arka arkaya → seamless loop */}
            <div className="cl-marquee-track flex gap-2 py-1 group-hover:[animation-play-state:paused]">
              {marqueeChips.map((el, i) => (
                <React.Fragment key={`a-${i}`}>{el}</React.Fragment>
              ))}
              {marqueeChips.map((el, i) => (
                <React.Fragment key={`b-${i}`}>{el}</React.Fragment>
              ))}
            </div>
            {/* Kenar gradyanları (fade) */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-[#0d1117] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-[#0d1117] to-transparent" />
          </div>
        )}
      </div>

      {/* Tüm ödüller modal */}
      {showAll && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4"
          onClick={() => setShowAll(false)}
          data-testid="cl-rewards-modal"
        >
          <div
            className="relative w-full max-w-2xl max-h-[90vh] rounded-2xl border border-white/10 bg-[#0d1117] shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-3 bg-gradient-to-r from-primary/15 to-transparent">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                <div className="text-sm font-bold">Tüm Koleksiyon Seviyesi Ödülleri</div>
              </div>
              <button
                onClick={() => setShowAll(false)}
                className="rounded-md p-1 hover:bg-white/10"
                aria-label="Kapat"
                data-testid="cl-rewards-modal-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-4 py-3 border-b border-white/10">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Toplam (Sv 1 → 2500)
              </div>
              <RewardsSummary list={all} />
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="cl-rewards-modal-list">
                {all.map((r) => (
                  <RewardCard key={r.level} r={r} compact />
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 px-4 py-2 text-[10px] text-muted-foreground text-center">
              Not: 20–1500 arası her 10 seviyede, 1500–2500 arası her 5 seviyede ödül verilir.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
